import React, { useEffect, useState } from 'react'
import { runInAction } from 'mobx'
import { observer } from 'mobx-react-lite'
import { useNavigate } from 'react-router-dom'
import { useStore } from '@store/StoreProvider'
import { store } from '@store/Store'
import { toastStore } from '@store/ToastStore'
import { handleUnauthorized } from '@utils/apiHelpers'
import { markTransferRequestHandledLocally } from '@utils/operatorTransferNotifications'
import { t } from '@store/I18nStore'
import { timeShort } from '@utils/dateTime'

/** Global accept/decline modal — WS handling lives in DialogsCountersProvider. */
export const GlobalTransferModal: React.FC = observer(() => {
  const { auth, i18n } = useStore()
  const navigate = useNavigate()
  const transfers = store.operatorTransfers

  // Client-side TTL: unlock sender composer / dismiss modal even if expired WS is missing.
  useEffect(() => {
    if (!auth.isAuthenticated) return
    const tick = () => {
      runInAction(() => transfers.clearExpired())
    }
    tick()
    const id = window.setInterval(tick, 1000)
    return () => window.clearInterval(id)
  }, [auth.isAuthenticated, transfers])

  const respond = async (requestId: number, action: 'accept' | 'decline') => {
    if (transfers.actionInFlight === requestId) return
    const request = transfers.incoming.find((r) => r.requestId === requestId)
    runInAction(() => transfers.setActionInFlight(requestId))
    const base = (i18n.apiBaseUrl || '').replace(/\/$/, '')
    const endpoint = action === 'accept' ? 'accept' : 'decline'
    try {
      const res = await fetch(`${base}/api/operator/dialog-transfer/${requestId}/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...auth.authHeader },
      })
      if (!res.ok) {
        handleUnauthorized(res)
        toastStore.show(
          action === 'accept' ? t('errors.chat.accept_transfer') : t('errors.chat.decline_transfer'),
          'error',
        )
        return
      }
      markTransferRequestHandledLocally(requestId)
      runInAction(() => {
        transfers.removeByRequestId(requestId)
        if (request) transfers.clearOutgoingPending(String(request.chatId))
      })
      if (action === 'accept' && request) {
        const chatId = String(request.chatId)
        runInAction(() => transfers.setPendingOpenChatId(chatId))
        navigate('/dialogs')
      }
    } catch (err) {
      console.error('[GlobalTransferModal] Transfer response error:', err)
      toastStore.show(t('errors.network'), 'error')
    } finally {
      runInAction(() => {
        if (transfers.actionInFlight === requestId) transfers.setActionInFlight(null)
      })
    }
  }

  if (!auth.isAuthenticated || transfers.incoming.length === 0) return null

  return (
    <>
      {transfers.incoming.map((req) => (
        <div key={req.requestId} className="ban-modal-backdrop transfer-request-backdrop" role="presentation">
          <div
            className="ban-modal transfer-request-modal"
            role="dialog"
            aria-modal="true"
            aria-label={t('chat.transfer.request')}
            onClick={(e) => e.stopPropagation()}
          >
            <h3>{t('chat.transfer.title')}</h3>
            <p className="transfer-request-text">
              {req.text || t('chat.transfer.incoming', { name: req.fromOperatorName }) + (req.chatTitle ? ` ${req.chatTitle}.` : '.')}
            </p>
            {req.expiresAt && <TransferCountdown expiresAt={req.expiresAt} />}
            <div className="ban-modal-actions">
              <button
                type="button"
                className="ghost-btn"
                onClick={() => respond(req.requestId, 'decline')}
                disabled={transfers.actionInFlight === req.requestId}
              >{t('app.actions.decline')}</button>
              <button
                type="button"
                className="primary-btn"
                onClick={() => respond(req.requestId, 'accept')}
                disabled={transfers.actionInFlight === req.requestId}
              >
                {transfers.actionInFlight === req.requestId ? t('app.loading.processing') : t('chat.accept')}
              </button>
            </div>
          </div>
        </div>
      ))}
    </>
  )
})

const TransferCountdown: React.FC<{ expiresAt: string }> = ({ expiresAt }) => {
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null)
  const effectiveExpiresAt = expiresAt?.trim() || ''

  useEffect(() => {
    if (!effectiveExpiresAt) {
      setSecondsLeft(null)
      return
    }
    const tick = () => {
      const diff = Math.ceil((new Date(effectiveExpiresAt).getTime() - Date.now()) / 1000)
      setSecondsLeft(diff > 0 ? diff : 0)
    }
    tick()
    const id = window.setInterval(tick, 1000)
    return () => window.clearInterval(id)
  }, [effectiveExpiresAt])

  if (secondsLeft == null) return null
  if (secondsLeft <= 0) {
    return <div className="transfer-countdown transfer-countdown-expired">{t('errors.timeout')}</div>
  }
  return <div className="transfer-countdown">{t('billing.trial.remaining_label')}: {secondsLeft} {timeShort('sec')}.</div>
}
